// Slot "map" view — pure, jsdom-free mappers (vitest-covered) for the per-slot declared-stage
// stepper. Plan: ao_node_worker_parity_click_through_program_2026_09_26.md ([UI] "Slot map view").
// Renders the backend `SlotView.stage` read model exactly as served; nothing here invents a stage.

import type { SlotStage, SlotView, StageSource } from "./types";

export type StageFilter = "all" | "await_operator" | "gate_submitted" | "debug";

export const STAGE_FILTER_OPTIONS: ReadonlyArray<{ value: StageFilter; label: string }> = [
  { value: "all", label: "All stages" },
  { value: "await_operator", label: "await_operator" },
  { value: "gate_submitted", label: "gate_submitted" },
  { value: "debug", label: "debug" },
];

/** Strict equality on the served `stage.step_type`; a slot with no `stage` never matches a
 * specific filter (absence is not a stage), only "all". */
export function matchesStageFilter(slot: Pick<SlotView, "stage">, filter: StageFilter): boolean {
  if (filter === "all") return true;
  return slot.stage?.step_type === filter;
}

export function filterSlotsByStage<T extends Pick<SlotView, "stage">>(slots: T[], filter: StageFilter): T[] {
  return filter === "all" ? slots : slots.filter((s) => matchesStageFilter(s, filter));
}

export type StepCellState = "done" | "current" | "pending" | "failed" | "skipped";

export interface StepCell {
  key: string;
  label: string;
  state: StepCellState;
  title: string;
}

export type ChipTone = "ok" | "bad" | "pending";

export interface TierChip {
  tier: "T1" | "T2";
  label: string;
  tone: ChipTone;
  title: string;
}

export type StageMapModel =
  | { kind: "absent" }
  | {
      kind: "map";
      cells: StepCell[];
      currentLabel: string;
      timeInStage: string;
      source: StageSource | null;
      chips: TierChip[];
      undeclared: boolean;
      expandable: boolean;
    };

/** "3m", "1h 05m", "2d 4h"; "—" for a missing/unparseable timestamp. Never negative (clock skew). */
export function formatTimeInStage(since: string | null | undefined, nowMs: number): string {
  if (!since) return "—";
  const t = Date.parse(since);
  if (Number.isNaN(t)) return "—";
  const secs = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, "0")}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** T1/T2 chips from the ref-derived stage only (`source: "ref"`): `landed` implies a green verdict. */
export function tierChips(stage: SlotStage): TierChip[] {
  if (stage.source !== "ref") return [];
  const t1Green: TierChip = {
    tier: "T1",
    label: "T1 ✓",
    tone: "ok",
    title: "T1 gate verdict green (refs/qg-verdict)",
  };
  switch (stage.step_type) {
    case "gate_green":
      return [t1Green];
    case "gate_red":
      return [{ tier: "T1", label: "T1 ✗", tone: "bad", title: "T1 gate verdict red (refs/qg-verdict)" }];
    case "landed":
      return [
        t1Green,
        { tier: "T2", label: "T2 …", tone: "pending", title: "Landed on the integration branch; awaiting promote" },
      ];
    case "promoted":
      return [t1Green, { tier: "T2", label: "T2 ✓", tone: "ok", title: "Promoted (T2 complete)" }];
    default:
      return [];
  }
}

function cellState(status: string | null | undefined, isCurrent: boolean): StepCellState {
  if (status === "failed") return "failed";
  if (status === "skipped") return "skipped";
  if (isCurrent || status === "active") return "current";
  if (status === "done") return "done";
  return "pending";
}

/** Stepper cells: the declared `steps` when the backend serves them, else `index/total` placeholders. */
export function stepCells(stage: SlotStage): StepCell[] {
  const steps = stage.steps ?? [];
  if (steps.length > 0) {
    return steps.map((st) => {
      const isCurrent = stage.current_step != null && st.n === stage.current_step;
      return {
        key: `step-${st.n}`,
        label: st.type,
        state: cellState(st.status, isCurrent),
        title: `${st.n}. ${st.type}${st.summary ? ` — ${st.summary}` : ""} (${st.status})`,
      };
    });
  }
  const total = stage.total ?? 0;
  const index = stage.index ?? 0;
  if (total <= 0) return [];
  return Array.from({ length: total }, (_, i) => {
    const n = i + 1;
    const state: StepCellState = n < index ? "done" : n === index ? "current" : "pending";
    return {
      key: `step-${n}`,
      label: n === index && stage.step_type ? stage.step_type : String(n),
      state,
      title: `step ${n} of ${total}`,
    };
  });
}

export function stageMapModel(stage: SlotStage | null | undefined, nowMs: number): StageMapModel {
  if (!stage) return { kind: "absent" };
  const cells = stepCells(stage);
  return {
    kind: "map",
    cells,
    currentLabel: stage.step_type ?? "—",
    timeInStage: formatTimeInStage(stage.since, nowMs),
    source: stage.source ?? null,
    chips: tierChips(stage),
    undeclared: stage.undeclared === true,
    expandable: (stage.steps?.length ?? 0) > 0,
  };
}
